import React, { useEffect, useCallback, useState, useRef, useMemo } from 'react'
import { useSelector } from 'react-redux'
import Toolbar, { Item } from 'devextreme-react/toolbar'
import { Button } from 'devextreme-react/button'
import { Popup } from 'devextreme-react/popup'
import TreeList, { Column, TreeListRef } from 'devextreme-react/tree-list'

import { RootState } from 'slices'
import { useIxAuth } from 'contexts/auth-context'
import { Settings } from 'helpers/settings-enum'
import { SettingType } from 'helpers/setting-type-enum'
import { settingsMenuSelector } from 'slices/ui'
import { useUserSettingsUpdate } from 'hooks'
import { zeroPad } from 'helpers/zero-pad'

import client from 'api'

import './common-run-time.scss'

export const CommonRunTime = () => {
    const {value: ixCommonRuntime, id: ixCommonRuntimeId} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.ixCommonRuntime))
    
    const { user } = useIxAuth()
    const [yearsList, setYearsList] = useState<any>([])
    const [popupVisible, setPopupVisible] = useState(false)
    const storeCommonRuntimeData = useUserSettingsUpdate()
    const yearsRef = useRef<TreeListRef>(null)
    const firstRender = useRef(true)

    useEffect(() => {
        if (popupVisible && yearsList.length) {
            setTimeout(() => {
                (yearsRef.current as any)?.instance().navigateToRow(yearsList[ixCommonRuntime?.yearDataIndex] ? yearsList[ixCommonRuntime?.yearDataIndex].JAHR : yearsList[yearsList.length-1].JAHR)
            }, 500)
        }

    },[popupVisible, ixCommonRuntime?.yearDataIndex, yearsList])

    useEffect(() => {
        client.get(`/api/ixweb/wjahr/?action=gte&bukrnr=${ixCommonRuntime?.objNr}&min_jahr=0&max_jahr=99999&fields=bukreisid,jahr,wjbeginn,wjende`).then((res: any) => {
            setYearsList(res.data.reverse())
        })

    },[ixCommonRuntime?.objNr])

    useEffect(() => {
        if (yearsList.length) {
            if ( firstRender.current ) {
                firstRender.current = false
                return
            }

            const selectedYearIndex = yearsList.findIndex((element: any) => element.JAHR === ixCommonRuntime.year)
            let data: any = {}

            selectedYearIndex >= 0
                ? data = {...ixCommonRuntime, yearDataIndex: selectedYearIndex, year: yearsList[selectedYearIndex].JAHR}
                : ixCommonRuntime.yearDataIndex === 0
                    ? data = {...ixCommonRuntime, yearDataIndex: 0, year: yearsList[0].JAHR}
                    : data = {...ixCommonRuntime, yearDataIndex: yearsList.length-1, year: yearsList[yearsList.length-1].JAHR}
            storeCommonRuntimeData('common-run-time', Settings.ixCommonRuntime, data, ixCommonRuntimeId, SettingType.GlobalSetting)
        }

    },[yearsList, ixCommonRuntimeId, storeCommonRuntimeData])

    const onCellClick = useCallback((e: any) => {
        const data = {...ixCommonRuntime, yearDataIndex: e.row.dataIndex, year: yearsList[e.row.dataIndex].JAHR}

        storeCommonRuntimeData('common-run-time', Settings.ixCommonRuntime, data, ixCommonRuntimeId, SettingType.GlobalSetting)
        setPopupVisible(false)
    },[ixCommonRuntime, storeCommonRuntimeData, yearsList, ixCommonRuntimeId])

    const _selectedRowKeys = useMemo(() => {
        if (!yearsList || !yearsList.length) return []

        if (ixCommonRuntime?.yearDataIndex >= 0 && yearsList[ixCommonRuntime.yearDataIndex]) {
            return [yearsList[ixCommonRuntime.yearDataIndex].JAHR]
        } else {
            return [yearsList[yearsList.length - 1].JAHR]
        }
    }, [ixCommonRuntime, yearsList])

    return (
        <div id='ix-common-runtime'>
            <Popup
                visible={popupVisible}
                showTitle={true}
                showCloseButton={true}
                width={270}
                height={300}
                onHiding={() => setPopupVisible(false)}
                position={ {at: 'right top', my: 'right bottom', of: '#toolbar'}}
                shading={false}
            >
                <TreeList
                    ref={yearsRef}
                    keyExpr='JAHR'
                    dataSource={yearsList}
                    showColumnHeaders={false}
                    showColumnLines={false}
                    showBorders={false}
                    showRowLines={false}
                    repaintChangesOnly={true}
                    dataStructure='plain'
                    width='100%'
                    height='100%'
                    className='years-list'
                    onCellClick={onCellClick}
                    selectedRowKeys={ _selectedRowKeys }
                    autoNavigateToFocusedRow={true}
                >
                    <Column alignment='center' width='auto' dataField='JAHR' dataType='number' sortOrder='desc'/>
                    <Column alignment='right' width='auto' cssClass='date-start' dataField='WJBEGINN' dataType='date' format={{day: '2-digit', month: '2-digit', year: 'numeric'}}/>
                    <Column alignment='center' width='auto' cssClass='separator' customizeText={() => {return '-'}}  dataType='string'/>
                    <Column alignment='left' width='auto' cssClass='date-end' dataField='WJENDE' dataType='date' format={{day: '2-digit', month: '2-digit', year: 'numeric'}}/>
                </TreeList>
            </Popup>

            <Toolbar className='common-run-time' id='toolbar'>
                <Item location='center'>
                    <span className='separator'>{user?.ixUserId}:{user?.user}</span>
                </Item>

                <Item location='after'>
                    <span className='separator'>{zeroPad(ixCommonRuntime?.objNr, 5)}</span>
                </Item>

                <Item location='after'>
                    <Button text='O+ P+ U+ C+ S+'/>
                </Item>

                <Item location='after'>
                    <Button text={`${yearsList[ixCommonRuntime?.yearDataIndex] ? yearsList[ixCommonRuntime?.yearDataIndex].JAHR : 'year'}`} onClick={() => setPopupVisible(true)}/>
                </Item>
            </Toolbar>
        </div>
    )
}
